import React, { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";

function finiteLimit(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function rangeHelper(field) {
  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  if (min === undefined && max === undefined) return undefined;

  const unitMatch = String(field?.label || "").match(/\(([^)]+)\)\s*$/);
  const unit = unitMatch?.[1] ? ` ${unitMatch[1]}` : "";
  if (min !== undefined && max !== undefined) return `Permitido: ${min}–${max}${unit}`;
  if (min !== undefined) return `Mínimo: ${min}${unit}`;
  return `Máximo: ${max}${unit}`;
}

function NumberField({ field, value, disabled, onCommit }) {
  const externalValue = value === undefined || value === null ? "" : String(value);
  const [draft, setDraft] = useState(externalValue);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    setDraft(externalValue);
    setEdited(false);
  }, [externalValue]);

  const min = finiteLimit(field?.limits?.min);
  const max = finiteLimit(field?.limits?.max);
  const step = finiteLimit(field?.limits?.step);

  const commitDraft = () => {
    if (!edited) return;

    if (draft === "") {
      setEdited(false);
      onCommit?.(field.path, "");
      return;
    }

    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(externalValue);
      setEdited(false);
      return;
    }

    let normalized = parsed;
    if (min !== undefined) normalized = Math.max(min, normalized);
    if (max !== undefined) normalized = Math.min(max, normalized);

    const normalizedDraft = String(normalized);
    setDraft(normalizedDraft);
    setEdited(false);
    onCommit?.(field.path, normalized);
  };

  return (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={field.label}
      value={draft}
      disabled={disabled}
      helperText={rangeHelper(field)}
      inputProps={{
        ...(min !== undefined ? { min } : {}),
        ...(max !== undefined ? { max } : {}),
        ...(step !== undefined ? { step } : {}),
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        setEdited(true);
      }}
      onBlur={commitDraft}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        event.target.blur();
      }}
    />
  );
}

export default function NavbarMobileElementEditor({ descriptor, form, disabled, onCommit }) {
  const fields = descriptor?.fields || [];
  return (
    <Stack spacing={1.1}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>Los valores vacíos siguen heredando la configuración de escritorio.</Typography>
      {fields.map((field) => {
        const value = getValueAtDescriptorPath(form, field.path, "");
        if (field.type === SEGMENT_FIELD_TYPES.SELECT) return <FormControl key={field.path} fullWidth size="small" disabled={disabled}><InputLabel>{field.label}</InputLabel><Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>{(field.options || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>;
        if (field.type === SEGMENT_FIELD_TYPES.NUMBER) return <NumberField key={field.path} field={field} value={value} disabled={disabled} onCommit={onCommit} />;
        return <TextField key={field.path} fullWidth size="small" type="text" label={field.label} value={value ?? ""} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.value)} />;
      })}
    </Stack>
  );
}
